import { useFormContext } from 'react-hook-form';
import { useRowsCreatives } from 'features/creatives';
import type { FC } from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import { Box, Divider, Tooltip, Typography } from '@mui/material';
import {
  AddPackTagButton,
  Checkbox,
  ChipInput,
  CircularProgress,
  PacksWithCreatives,
  SuspenseErrorFallback,
  Switch,
} from 'components/common';
import { DataGrid, type GridRenderCellParams } from '@mui/x-data-grid';
import { SelectAdTypes } from './FacebookFormComponents';
import type { GridRowSelectionModel } from '@mui/x-data-grid/models/gridRowSelectionModel';
import { FormContainer } from 'components/layout';
import type { CreativeEntry, PackEntry } from 'types/shared';
import { useAdTypes } from '../api/getAdTypes';
import { isEmpty } from 'lodash-es';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import { Network } from '../../../constants';

const CREATIVES_LIMIT = 30;

const CreativesGrid = () => {
  const { watch, setValue, trigger } = useFormContext();
  const { rows, isLoading, isError, error } = useRowsCreatives({
    params: {
      network: Network.FACEBOOK,
    },
  });

  const adType = watch('adType') as string;
  const isDLO = adType?.toLowerCase() === 'dlo';
  const platform = watch('platform') as string;
  const selectedCreatives = watch('selectedCreatives') as string[];
  const selectedOnly = watch('selectedCreativesOnly') as string[];
  const packsWithCreatives = watch('packsWithCreatives') as PackEntry[];
  const activePacksWithCreatives = watch('activePacksWithCreatives') as number;

  const { data: adTypes } = useAdTypes({
    params: { platform },
    config: {
      enabled: !!platform,
    },
  });

  const COLUMNS = useMemo(() => {
    const currentAdTypeValue =
      adTypes?.find((i) => i.code === adType)?.name ?? adType;

    return [
      { field: 'creative_id', headerName: 'Creative ID', width: 120 },
      {
        field: 'name',
        headerName: 'Name',
        width: 450,
        renderCell: (params: GridRenderCellParams<CreativeEntry>) => {
          const isSelectable =
            (isDLO && !isEmpty(params.row.locales)) || !isDLO;
          const tooltipMessage = !isSelectable
            ? `Can't be used for ${currentAdTypeValue} without localizations`
            : null;

          return (
            <>
              <Tooltip title={tooltipMessage} arrow placement={'top'}>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 0.25,
                    height: '100%',
                    flexDirection: 'column',
                    justifyContent: 'center',
                  }}
                >
                  <Typography variant={'body2'}>{params.row.name}</Typography>
                  <Typography
                    variant={'caption'}
                    sx={(t) => ({ color: t.palette.text.disabled })}
                  >
                    {isSelectable && !isEmpty(params.row.locales)
                      ? `Loc: ${params.row.locales?.join(', ')}`
                      : 'No localizations'}
                  </Typography>
                </Box>
              </Tooltip>
            </>
          );
        },
      },
      { field: 'app', headerName: 'App', width: 120 },
      { field: 'creative_type', headerName: 'Type', width: 120 },
      { field: 'created', headerName: 'Created', width: 120 },
    ];
  }, [adType, adTypes, isDLO]);

  const filteredRows = useMemo(() => {
    let filtered = rows;

    if (selectedOnly) {
      filtered = filtered?.filter((row) => selectedCreatives.includes(row.id));
    }

    return filtered;
  }, [rows, selectedCreatives, selectedOnly]);

  const onSelectionModelChange = useCallback(
    (selectedRows: GridRowSelectionModel) => {
      const parsedSelectedRows = selectedRows.slice(0, CREATIVES_LIMIT);

      setValue(
        'packsWithCreatives',
        [...packsWithCreatives].map((pack, index) =>
          index === activePacksWithCreatives
            ? { ...pack, creatives: parsedSelectedRows.map(String) }
            : pack,
        ), { shouldValidate: true },
      );
      setValue('selectedCreatives', parsedSelectedRows, { shouldValidate: true });
    },
    [packsWithCreatives, activePacksWithCreatives, setValue],
  );

  useEffect(() => {
    void trigger('packsWithCreatives');
    void trigger('selectedCreatives');
  }, [trigger]);

  if (isError) {
    return (
      <SuspenseErrorFallback
        title={'Choose Creatives'}
        message={
          (
            error as {
              cause?: { error?: string };
            }
          )?.cause?.error ?? 'An error occurred while fetching creatives'
        }
      />
    );
  }

  if (isLoading && isEmpty(filteredRows)) {
    return <CircularProgress title={'Creatives are loading...'} />;
  }

  if (isEmpty(filteredRows)) {
    return (
      <Typography
        data-testid="noCreativesPlaceholder"
        sx={(t) => ({
          color: t.palette.grey[400],
          fontSize: t.typography.h4.fontSize,
        })}
      >
        No creatives found
      </Typography>
    );
  }

  return (
    <Box style={{ position: 'relative', height: 380, width: '100%' }}>
      {isLoading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            display: 'flex',
            alignContent: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            zIndex: 2,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
          }}
        >
          <CircularProgress title={'Creatives are loading...'} />
        </Box>
      )}
      <DataGrid
        rows={filteredRows ?? []}
        columns={COLUMNS}
        localeText={{
          noRowsLabel: !adType ? 'Select Ad type to add creatives' : '',
        }}
        checkboxSelection
        rowSelectionModel={selectedCreatives ?? []}
        onRowSelectionModelChange={onSelectionModelChange}
        keepNonExistentRowsSelected
        disableVirtualization
        hideFooterPagination
        getRowId={(row) => row.id}
        isRowSelectable={(params) => {
          const isDLOLocalesEmpty = isDLO && !isEmpty(params.row.locales);
          const isUpToLimit =
            selectedCreatives.length < CREATIVES_LIMIT ||
            selectedCreatives.includes(params.row.id);
          return isDLOLocalesEmpty || !isDLO || isUpToLimit;
        }}
        slots={{
          footer: () =>
            rows?.length ? (
              <Box
                sx={(t) => ({
                  p: 2,
                  height: 54,
                  minHeight: 54,
                  display: 'flex',
                  alignItems: 'center',
                  gap: .5,
                  borderTop: `1px solid ${t.palette.divider}`,
                  cursor: 'default',
                })}
              >
                <Typography variant={'body2'}>
                  {rows?.length} found /
                </Typography>
                {selectedCreatives?.length === CREATIVES_LIMIT ? (
                  <>
                    <InfoIcon color={'primary'} sx={{ fontSize: 21 }} />
                    <Typography
                      variant={'body2'}
                    >{`Up to ${CREATIVES_LIMIT} creatives can be selected`}</Typography>
                  </>
                ) : (
                  <Typography variant={'body2'}>{selectedCreatives.length} selected</Typography>
                )}
              </Box>
            ) : null,
        }}
      />
    </Box>
  );
};

const CreativesForm: FC = () => {
  const { watch } = useFormContext();
  const adType = watch('adType') as string;
  const marketingProcess = watch('marketingProcess') as string;
  const isMarketingProcessInbox = marketingProcess?.includes('inbox');
  const activePacksWithCreatives = watch('activePacksWithCreatives') as number;

  return (
    <FormContainer
      title={adType ? 'Choose Creatives' : 'Select Ad type'}
      rightSideElement={<SelectAdTypes />}
      sx={{ flexDirection: 'column', gap: 2 }}
    >
      <Box
        sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}
      >
        <Box
          sx={{ display: 'flex', flexDirection: 'row', gap: 2, width: '100%' }}
        >
          <PacksWithCreatives />
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            gap: 2,
            width: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              flex: 1,
              flexDirection: 'row',
              alignItems: 'center',
              flexWrap: 'nowrap',
            }}
          >
            <ChipInput
              name={`packsWithCreatives.${activePacksWithCreatives}.filterCreativeByName`}
              inputLabel="Enter creative name"
            />
            <Checkbox
              dataTestId={'selectedCreativesOnly'}
              name={'selectedCreativesOnly'}
              label={'Selected only'}
              controlLabelSx={{ whiteSpace: 'nowrap' }}
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'end',
              flex: 1,
              gap: 2,
            }}
          >
            {!isMarketingProcessInbox && (
              <>
                <Divider
                  sx={{ mx: 1 }}
                  orientation="vertical"
                  variant="middle"
                  flexItem
                />
                {
                  <Switch
                    sx={{ m: 0, p: 0 }}
                    key={`packsWithCreatives.${activePacksWithCreatives}.visual_touch_ups`}
                    name={`packsWithCreatives.${activePacksWithCreatives}.visual_touch_ups`}
                    label={'Visual touch-ups'}
                  />
                }
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'end',
                    alignItems: 'start',
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      minHeight: 32,
                      mt: 0.5,
                    }}
                  >
                    <AddPackTagButton />
                  </Box>
                </Box>
              </>
            )}
          </Box>
        </Box>
        <CreativesGrid />
      </Box>
    </FormContainer>
  );
};

export default CreativesForm;
